import { skipToken, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  Department_Read,
  getV1CompaniesCompanyIdDepartmentsTypeahead,
  getV1CompaniesCompanyIdMaterialTypeahead,
  getV1CompaniesCompanyIdSiteTypeahead,
  getV1CompaniesCompanyIdUsersTypeahead,
  GetV1CompaniesCompanyIdUsersTypeaheadData,
  User_Read,
} from '@treadinc/horizon-api-spec';
import { useCallback, useMemo, useState } from 'react';

import { FilterMenuItemLoadingReason } from '~components/Filters/FilterMenuItem';
import { FilterMenuOption } from '~components/Filters/FilterMenuItem';
import { SystemRoles } from '~constants/enums';
import { Department } from '~hooks/useDepartment/models';
import { MaterialTypeahead } from '~hooks/useMaterials/models';
import { SiteTypeahead } from '~hooks/useSites/models';
import { User } from '~hooks/useUsers/models';
import { OrderDispatchQuery } from '~pages/Dispatch/components/filters/orders-dispatch-filters-queries/OrdersDispatchFiltersQueries';
import { extractPagination, Pagination } from '~services/pagination';

const QUERY_PAGINATION_LIMIT = 10;

type QueryParamsState = {
  shouldFetch: boolean;
  filters: { query: string };
  pagination?: Pagination & { hasMore: boolean };
};

type SimpleQueryParams = Pick<
  NonNullable<GetV1CompaniesCompanyIdUsersTypeaheadData['query']>,
  'page[after]' | 'page[limit]' | 'search[query]'
>;

function deriveSimpleQueryParams(queryParams: QueryParamsState) {
  const { filters, pagination } = queryParams;

  const query: SimpleQueryParams = {
    'page[limit]': QUERY_PAGINATION_LIMIT,
  };

  if (pagination?.after) {
    query['page[after]'] = pagination.after;
  }

  if (filters.query.trim().length) {
    query['search[query]'] = filters.query.trim();
  }

  return query;
}

type TypeaheadFetchFn = (params: {
  companyId: string;
  query: SimpleQueryParams;
  signal: AbortSignal;
}) => Promise<{ data: FilterMenuOption[]; pagination: Pagination }>;

function useTypeaheadQuery(
  companyId: string,
  queryKeyName: string,
  fetchFn: TypeaheadFetchFn,
): OrderDispatchQuery {
  const queryKey = `ProjectsFiltersQueries_${queryKeyName}`;
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<QueryParamsState>({
    shouldFetch: false,
    filters: { query: '' },
  });

  const { isFetching, isFetchingNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters.query, companyId],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: queryParams.shouldFetch
      ? ({ pageParam, signal }) => {
          const query = deriveSimpleQueryParams({
            ...queryParams,
            pagination: pageParam,
          });

          return fetchFn({
            companyId,
            query,
            signal,
          }).then(({ data, pagination }) => {
            setQueryParams((state) => ({
              ...state,
              pagination: {
                ...pagination,
                hasMore: Boolean(pagination.after),
              },
            }));

            return data;
          });
        }
      : skipToken,
  });

  const allData = useMemo(() => (data?.pages ?? []).flat(), [data?.pages]);

  const loadingReason = useMemo(() => {
    if (isFetchingNextPage) {
      return FilterMenuItemLoadingReason.INFINITE_SCROLL;
    }

    if (isFetching) {
      return FilterMenuItemLoadingReason.SEARCH_VALUE;
    }
  }, [isFetchingNextPage, isFetching]);

  const fetch = useCallback((searchQuery: string) => {
    setQueryParams({ shouldFetch: true, filters: { query: searchQuery } });
  }, []);

  const fetchMore = useMemo(() => {
    if (queryParams.pagination?.hasMore) {
      return fetchNextPage;
    }
  }, [queryParams.pagination?.hasMore, fetchNextPage]);

  const clear = useCallback(() => {
    setQueryParams({ shouldFetch: false, filters: { query: '' } });

    queryClient.setQueriesData({ queryKey: [queryKey] }, () => ({
      pages: [],
      pageParams: [],
    }));
  }, []);

  return { clear, data: allData, fetch, fetchMore, loadingReason };
}

const fetchUsersTypeahead: TypeaheadFetchFn = async ({ companyId, query, signal }) => {
  const response = await getV1CompaniesCompanyIdUsersTypeahead({
    path: { 'company-id': companyId },
    query,
    signal,
  });

  const data = response.data.data.map((user) => {
    const parsed = User.parse(user as User_Read);
    return { label: parsed.fullName, value: parsed.id };
  });

  return { data, pagination: extractPagination(response) };
};

const fetchSalespersonsTypeahead: TypeaheadFetchFn = async ({
  companyId,
  query,
  signal,
}) => {
  const response = await getV1CompaniesCompanyIdUsersTypeahead({
    path: { 'company-id': companyId },
    query: { ...query, 'filter[iam_roles]': [SystemRoles.SALESPERSON] },
    signal,
  });

  const data = response.data.data.map((user) => {
    const parsed = User.parse(user as User_Read);
    return { label: parsed.fullName, value: parsed.id };
  });

  return { data, pagination: extractPagination(response) };
};

const fetchDepartmentsTypeahead: TypeaheadFetchFn = async ({
  companyId,
  query,
  signal,
}) => {
  const response = await getV1CompaniesCompanyIdDepartmentsTypeahead({
    path: { 'company-id': companyId },
    query,
    signal,
  });

  const data = response.data.data.map((department) => {
    const parsed = Department.parse(department as Department_Read);
    return { label: parsed.name, value: parsed.id };
  });

  return { data, pagination: extractPagination(response) };
};

const fetchSitesTypeahead: TypeaheadFetchFn = async ({ companyId, query, signal }) => {
  const response = await getV1CompaniesCompanyIdSiteTypeahead({
    path: { 'company-id': companyId },
    query,
    signal,
  });

  const data = response.data.data.map((site) => {
    const parsed = SiteTypeahead.parse(site);
    return { label: parsed.name, value: parsed.id };
  });

  return { data, pagination: extractPagination(response) };
};

const fetchMaterialsTypeahead: TypeaheadFetchFn = async ({
  companyId,
  query,
  signal,
}) => {
  const response = await getV1CompaniesCompanyIdMaterialTypeahead({
    path: { 'company-id': companyId },
    query,
    signal,
  });

  const data = response.data.data.map((material) => {
    const parsed = MaterialTypeahead.parse(material);
    return { label: parsed.name, value: parsed.id };
  });

  return { data, pagination: extractPagination(response) };
};

export function useCollaboratorsQuery(companyId: string): OrderDispatchQuery {
  return useTypeaheadQuery(companyId, 'collaborators', fetchUsersTypeahead);
}

export function useForemenQuery(companyId: string): OrderDispatchQuery {
  return useTypeaheadQuery(companyId, 'foremen', fetchUsersTypeahead);
}

export function useRequestersQuery(companyId: string): OrderDispatchQuery {
  return useTypeaheadQuery(companyId, 'requesters', fetchUsersTypeahead);
}

export function useSalespersonsQuery(companyId: string): OrderDispatchQuery {
  return useTypeaheadQuery(companyId, 'salespersons', fetchSalespersonsTypeahead);
}

export function useDepartmentsQuery(companyId: string): OrderDispatchQuery {
  return useTypeaheadQuery(companyId, 'departments', fetchDepartmentsTypeahead);
}

export function usePickupSitesQuery(companyId: string): OrderDispatchQuery {
  return useTypeaheadQuery(companyId, 'pickupSites', fetchSitesTypeahead);
}

export function useDropoffSitesQuery(companyId: string): OrderDispatchQuery {
  return useTypeaheadQuery(companyId, 'dropoffSites', fetchSitesTypeahead);
}

export function useMaterialsQuery(companyId: string): OrderDispatchQuery {
  return useTypeaheadQuery(companyId, 'materials', fetchMaterialsTypeahead);
}
